import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
  useTheme
} from "@mui/material";

import { CircleCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DeleteProfileImageModalContentBox, StyledError
} from "./DeleteProfileImageModal.styles";
import { DeleteProfileImageModalProps } from "./DeleteProfileImageModal.types";

import {
  ButtonText,
  Icon,
  Spinner
} from "..";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { TitleTypography } from "../../components/CalendarCard/CalendarCard.style";
import { CommonModal } from "../../components/Commons/Modal.component";
import { useDeleteProfileImageQuery } from "../../queries";

export function DeleteProfileImageModal({
  closeDrawer,
  onSuccess,
  onClose,
  open,
  ...props
}: DeleteProfileImageModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    mutate: deleteProfileImage,
    isSuccess:deleteProfileImageSuccess,
    isPending:deleteProfileImageLoading,
    error: deleteProfileImageError,
    isError: deleteProfileImageIsError,
    isIdle: deleteProfileImageIdle,
    reset: deleteProfileImageReset
  } = useDeleteProfileImageQuery();

  const onCloseDrawer = () => {
    closeDrawer();
    setTimeout(() => {
      deleteProfileImageReset();
    }, 300);
  };

  return (
    <CommonModal
      open={ open }
      handleClose={ ()=> onClose }
      avoidMobileSetting={ true }
      sxModalContent={ {
        width:"fit-content"
      } }
    >
      <DeleteProfileImageModalContentBox
        id="container modal"
        container
        { ...props }
      >

        { deleteProfileImageLoading && <Spinner/> }
        { deleteProfileImageIsError && (
          <Stack gap={ "24px" }>
            <TitleTypography sx={ { fontSize: "20px" } }>{ t("error_occurred") }</TitleTypography>
            { typeof deleteProfileImageError === "string" && deleteProfileImageError != "" && (
              <Accordion>
                <AccordionSummary
                  expandIcon={ <Icon icon={ "dropdown" }/> }
                  aria-controls="deleteProfileImageErrorContent"
                  id="deleteProfileImageErrorHeader"
                >
                  <Typography component="span">{ t("error_details") }</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <StyledError>{ deleteProfileImageError }</StyledError>
                </AccordionDetails>
              </Accordion>
            ) }
            <Box alignSelf={ "center" }>
              <ButtonText onClick={ onCloseDrawer }
              >
                { t("close") }
              </ButtonText>
            </Box>
          </Stack>
        ) }
        { deleteProfileImageSuccess && (
          <Stack flexDirection={ "column" } gap={ "24px" }>
            <Stack
              alignItems={ "center" }
              flexDirection={ "column" }
              gap={ 3 }>
              <CircleCheck
                aria-hidden="true"
                size="3rem"
                color={ theme.customColors.exploreCardText }
              />
              <Typography 
                sx={ { textAlign: "center" } }
                fontSize={ "1rem" }
                fontWeight={ 500 }
              >
                { t("profile_delete_avatar_success") }
              </Typography>
            </Stack>
            <Box alignSelf={ "center" }>
              <ButtonDigited 
                typology="primary"
                onClick={ () =>{
                  onCloseDrawer();
                  onSuccess();
                } }
                text={ t("close") }
              />
            </Box>
          </Stack>
        ) }

        { deleteProfileImageIdle && !deleteProfileImageError && (
          <Stack gap={ "24px" }>
            <Typography
              sx={ {
                fontSize: "1.25rem",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "1.5rem",
                textAlign: "center"
              } }
            >
              { t("profile_delete_avatar_message") }
            </Typography>
            <Stack
              direction={ "row" }
              justifyContent={ "center" }
              gap={ "24px" }
            >
              <ButtonDigited 
                typology="primary"
                onClick={ () =>{
                  deleteProfileImage();
                } }
                text={ t("delete") }
              />
              <ButtonDigited 
                typology="secondary"
                onClick={ onCloseDrawer }
                text={ t("annul") }
              />
            </Stack>
          </Stack>
        ) }

      </DeleteProfileImageModalContentBox>
    </CommonModal>
  );

}
